import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { userService, licenseService } from '../services/api';
import { EditProfileModal, DeleteConfirmModal } from '../components/UserModals';
import { TenantAssignmentModal, EditPermissionsModal, MODULE_LABELS } from '../components/TenantComponents';

const UserDetail = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tenantAssignments, setTenantAssignments] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [licenseSummary, setLicenseSummary] = useState({ total: 0, available: 0, activated: 0, revoked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [editPermissionsAssignment, setEditPermissionsAssignment] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getById(username);
      setUser(response.data);
      
      // Tenant assignments come from the enhanced API response now
      if (response.data?.tenantAssignments) {
        setTenantAssignments(response.data.tenantAssignments);
      } else {
        // Fallback: fetch separately
        try {
          const tenantResponse = await userService.getTenants(username);
          setTenantAssignments(tenantResponse.data || []);
        } catch (err) {
          console.error('Error fetching tenant assignments:', err);
          setTenantAssignments([]);
        }
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      setError(err.response?.data?.error || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [username]);

  const fetchLicenses = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await licenseService.getAll({ 
        ownerType: 'user', 
        ownerId: user.id,
        pageSize: 100 
      });
      const licenseList = response.licenses || [];
      setLicenses(licenseList);
      
      // Calculate summary
      const summary = {
        total: licenseList.length,
        available: licenseList.filter(l => l.status === 'available').length,
        activated: licenseList.filter(l => l.status === 'activated').length,
        revoked: licenseList.filter(l => l.status === 'revoked').length,
      };
      setLicenseSummary(summary);
    } catch (err) {
      console.error('Error fetching licenses:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (user?.id) {
      fetchLicenses();
    }
  }, [user?.id, fetchLicenses]);

  const handleRemoveTenant = async (tenantId) => {
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      await userService.removeTenant(username, tenantId);
      setMessage('Tenant assignment removed successfully');
      fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove tenant assignment');
    } finally {
      setActionLoading(false);
    }
  };

  // The Grants panel's rows come from the SAME fetch as Tenant Assignments:
  // GET /users/:id/tenants nests per-module grants ({module, permission,
  // scope}) inside each tenant row — GET /users/:id has no grants key at all,
  // so tenantAssignments is the panel's only truthful source. Flattened here
  // into one row per (tenant, module), which is what the model calls a grant.
  const grantRows = tenantAssignments.flatMap((a) =>
    (a.grants || []).map((g) => ({
      tenantId: a.tenant_id,
      tenantName: a.tenant_name,
      module: g.module,
      level: g.permission,
      scope: g.scope,
    }))
  );

  // Inline level change for a single grant row. Sending the one-module map is
  // safe: POST /users/:id/tenants upserts ONLY the modules posted, so the
  // user's sibling grants on the tenant are untouched. Write-then-refetch like
  // every other mutation on this page — never optimistic.
  const handleGrantLevelChange = async (grant, level) => {
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      await userService.assignTenant(username, grant.tenantId, { [grant.module]: level });
      setMessage(`${grant.tenantName}: ${MODULE_LABELS[grant.module] || grant.module} set to ${level}`);
      fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update grant');
    } finally {
      setActionLoading(false);
    }
  };

  // Per-module revocation (V276): deletes exactly this module's row, unlike the
  // Tenant Assignments ✕ which drops the whole membership.
  const handleGrantRevoke = async (grant) => {
    // Owner-lockout guard: membership IS the grant rows, so deleting the LAST
    // module row on the user's own PERSONAL tenant strands them — the
    // workspace vanishes from their panels until an admin re-assigns them.
    // Super-admins may want exactly that, so this warns and asks; it never
    // blocks. (Confirm idiom: window.confirm guard-in-handler, as everywhere
    // else in this client.)
    const isLastPersonalGrant =
      grant.tenantId === user?.personalTenantId &&
      grantRows.filter((r) => r.tenantId === grant.tenantId).length === 1;
    if (isLastPersonalGrant && !window.confirm(
      'This is the last grant on their personal workspace — revoking it locks the owner out until an admin re-assigns them. Revoke anyway?'
    )) return;
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      await userService.removeModulePermission(user?.id || username, grant.tenantId, grant.module);
      setMessage(`${grant.tenantName}: ${MODULE_LABELS[grant.module] || grant.module} grant revoked`);
      fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke grant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    setMessage(null);
    setError(null);

    try {
      let response;
      switch (action) {
        case 'disable':
          response = await userService.disable(username);
          break;
        case 'enable':
          response = await userService.enable(username);
          break;
        case 'delete':
          response = await userService.delete(username);
          setShowDeleteConfirm(false);
          navigate('/users');
          return;
        default:
          return;
      }
      setMessage(response.message);
      fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  // "Sign out user" — revoke ALL of the user's sessions across every SSO product
  // (BM/pdooh.ai, galigali.in, Media, iot-admin) on every device. Best-effort
  // fetch of the active-session count first so the admin sees the impact in the
  // confirm; the window.confirm idiom matches handleGrantRevoke elsewhere here.
  const handleSignOut = async () => {
    let count = null;
    try {
      const s = await userService.getSessions(user?.id || username);
      count = s?.data?.activeSessions;
    } catch { /* count is a nicety; proceed even if it fails */ }

    const who = user?.email || user?.name || username;
    const impact = count === 0
      ? 'They have no active sessions right now.'
      : count != null
        ? `This ends ${count} active session${count === 1 ? '' : 's'}.`
        : '';
    const ok = window.confirm(
      `Sign ${who} out of ALL products (Brands Manager, galigali.in, Media, iot-admin) on every device?\n\n`
      + `${impact}\nTheir current access token may keep working up to ~30 min, then they must log in again.`
    );
    if (!ok) return;

    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await userService.revokeSessions(user?.id || username);
      setMessage(response.message);
      fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Sign out failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateProfile = async (data) => {
    setActionLoading(true);
    try {
      const response = await userService.update(username, data);
      setMessage(response.message);
      setShowEditModal(false);
      fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMigrate = async () => {
    if (!user?.id) return;
    setActionLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await userService.migrate(user.id);
      setMessage(response.message || 'Personal workspace created');
      fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Migration failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      ACTIVE: { class: 'status-active', label: 'Active', desc: 'Can login normally' },
      PENDING: { class: 'status-pending', label: 'Pending', desc: 'Awaiting first login' },
      DISABLED: { class: 'status-disabled', label: 'Disabled', desc: 'Account suspended' },
    };
    return badges[status] || { class: '', label: status, desc: '' };
  };

  const getLicenseStatusBadge = (status) => {
    const badges = {
      'unowned': { class: 'status-pending', label: 'Unowned' },
      'available': { class: 'status-pending', label: 'Available' },
      'activated': { class: 'status-active', label: 'Activated' },
      'revoked': { class: 'status-disabled', label: 'Revoked' },
    };
    return badges[status] || { class: '', label: status };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="user-detail-page">
        <div className="page-header">
          <Link to="/users" className="back-link">← Back to Users</Link>
        </div>
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(user?.status);

  return (
    <div className="user-detail-page">
      <div className="page-header">
        <Link to="/users" className="back-link">← Back to Users</Link>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
            Edit Profile
          </button>
          <div className="actions-dropdown">
            <button className="btn btn-secondary">
              {actionLoading ? '...' : 'Actions ▼'}
            </button>
            <div className="dropdown-menu">
              {user?.status !== 'DISABLED' ? (
                <button className="dropdown-item text-warning" onClick={() => handleAction('disable')}>
                  Disable Account
                </button>
              ) : (
                <button className="dropdown-item text-success" onClick={() => handleAction('enable')}>
                  Enable Account
                </button>
              )}
              <button className="dropdown-item text-warning" onClick={handleSignOut}>
                Sign out (all sessions)
              </button>
              <button className="dropdown-item text-danger" onClick={() => setShowDeleteConfirm(true)}>
                Delete User
              </button>
            </div>
          </div>
        </div>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* User Profile Card */}
      <div className="user-profile-card">
        <div className="profile-header">
          <div className="user-avatar large" aria-label={`Avatar for ${user?.name || user?.email}`}>
            {user?.name?.charAt(0) || user?.email?.charAt(0) || '?'}
          </div>
          <div className="profile-info">
            <h1>{user?.name || 'No name'}</h1>
            <p className="email">{user?.email}</p>
            <span className={`status-badge ${statusBadge.class}`}>
              {statusBadge.label}
            </span>
            <span className="status-desc">{statusBadge.desc}</span>
          </div>
        </div>
      </div>

      {/* Account Info Section */}
      <div className="detail-section">
        <h2>Account Information</h2>
        <div className="detail-grid">
          <div className="detail-item">
            <label>User ID</label>
            <span className="mono">{user?.id || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Email</label>
            <span>{user?.email || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Phone</label>
            <span>{user?.phone || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <label>Super Admin</label>
            <span>{user?.isSuperAdmin ? 'Yes' : 'No'}</span>
          </div>
          <div className="detail-item">
            <label>Auth Method</label>
            <span>OTP (login.galigali.in)</span>
          </div>
          <div className="detail-item">
            <label>Personal Workspace</label>
            <span>
              {user?.personalTenantId ? (
                <Link to={`/tenants/${user.personalTenantId}`} style={{ color: '#228b22' }}>
                  {user.personalTenantName || 'Personal Account'}
                </Link>
              ) : (
                <span style={{ color: '#e65100' }}>
                  Not provisioned{' '}
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={handleMigrate} 
                    disabled={actionLoading}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    {actionLoading ? '...' : 'Create'}
                  </button>
                </span>
              )}
            </span>
          </div>
          <div className="detail-item">
            <label>Migration Status</label>
            <span>
              {user?.migrationStatus === 'migrated' ? (
                <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#e6f7e6', color: '#228b22', fontSize: '0.85rem' }}>
                  Migrated
                </span>
              ) : (
                <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#fff3e0', color: '#e65100', fontSize: '0.85rem' }}>
                  Legacy — Pending Migration
                </span>
              )}
            </span>
          </div>
          <div className="detail-item">
            <label>Created</label>
            <span>{formatDate(user?.createdAt)}</span>
          </div>
          <div className="detail-item">
            <label>Last Updated</label>
            <span>{formatDate(user?.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Tenant Assignments Section */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Tenant Assignments</h2>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowTenantModal(true)}>
            + Add Tenant
          </button>
        </div>
        {tenantAssignments.length === 0 ? (
          <div className="empty-state-small">
            <p>No tenant assignments yet.</p>
            <p className="muted">Click "Add Tenant" to assign this user to a tenant.</p>
          </div>
        ) : (
          <div className="tenant-list">
            {tenantAssignments.map((assignment, index) => (
              <div key={index} className="tenant-assignment-item">
                <div className="tenant-info">
                  <Link to={`/tenants/${assignment.tenant_id}`} className="tenant-name">
                    {assignment.tenant_name}
                  </Link>
                  {assignment.is_personal && (
                    <span style={{ fontSize: '0.7rem', padding: '1px 4px', borderRadius: '3px', background: '#e3f2fd', color: '#1565c0', marginLeft: '0.5rem' }}>personal</span>
                  )}
                </div>
                <div className="tenant-role" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {assignment.permissions ? (
                    Object.entries(assignment.permissions).map(([mod, level]) => {
                      const colors = { admin: '#dc3545', editor: '#fd7e14', viewer: '#6c757d' };
                      return (
                        <span key={mod} style={{
                          display: 'inline-block', padding: '2px 6px', borderRadius: '4px',
                          fontSize: '0.75rem', background: `${colors[level] || '#6c757d'}15`,
                          color: colors[level] || '#6c757d', border: `1px solid ${colors[level] || '#6c757d'}30`,
                        }}>
                          {mod}: {level}
                        </span>
                      );
                    })
                  ) : (
                    <span className="role-badge">no permissions</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditPermissionsAssignment(assignment)}
                    disabled={actionLoading}
                    title="Edit permissions"
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveTenant(assignment.tenant_id)}
                    disabled={actionLoading}
                    title="Remove from tenant"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Grants Section (V276) — the fine-grained per-module view. Tenant
          Assignments above stays the add/remove surface; this panel edits one
          grant at a time and can revoke a single module without touching the
          membership. Both render the same DB rows and both refetch after every
          write, so they can only disagree for the length of one fetch. */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Grants</h2>
        </div>
        {grantRows.length === 0 ? (
          <div className="empty-state-small">
            <p>No product grants recorded.</p>
            {/* Membership IS the grant rows, so a fully-revoked user has no
                tenants left anywhere on this page — the re-add path must live
                at the exact dead end. Opens the same TenantAssignmentModal as
                '+ Add Tenant' above; its picker includes personal tenants, so
                this also recovers a locked-out personal-workspace owner. */}
            <button className="btn btn-secondary btn-sm" onClick={() => setShowTenantModal(true)}>
              Assign to a workspace
            </button>
          </div>
        ) : (
          <div className="data-table" style={{ fontSize: '0.85rem' }}>
            <table>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Product</th>
                  <th>Level</th>
                  <th>Scope</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {grantRows.map((g) => (
                  <tr key={`${g.tenantId}-${g.module}`}>
                    <td>
                      <Link to={`/tenants/${g.tenantId}`}>{g.tenantName}</Link>
                    </td>
                    <td>{MODULE_LABELS[g.module] || g.module}</td>
                    <td>
                      <select
                        value={g.level}
                        disabled={actionLoading}
                        onChange={e => handleGrantLevelChange(g, e.target.value)}
                      >
                        {['admin', 'editor', 'viewer'].map(l => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {/* Scope has no consumer yet (V276 reserves the column) —
                          display-only, never editable here. */}
                      {g.scope ? (
                        <span
                          title={JSON.stringify(g.scope)}
                          style={{ fontSize: '0.7rem', padding: '1px 5px', borderRadius: 4, background: '#fff3e0', color: '#e65100', cursor: 'help' }}
                        >
                          scoped
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleGrantRevoke(g)}
                        disabled={actionLoading}
                        title="Revoke this module grant only"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Licenses Section */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Licenses</h2>
          <Link to="/licenses" className="btn btn-secondary btn-sm">
            View All Licenses
          </Link>
        </div>
        
        {/* License Summary */}
        <div className="license-summary" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#f5f5f5', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{licenseSummary.total}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Total</div>
          </div>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#fff9e6', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#b8860b' }}>{licenseSummary.available}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Available</div>
          </div>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#e6f7e6', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#228b22' }}>{licenseSummary.activated}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Activated</div>
          </div>
          <div className="summary-card" style={{ padding: '0.75rem 1rem', background: '#ffe6e6', borderRadius: '8px', minWidth: '100px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#dc3545' }}>{licenseSummary.revoked}</div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Revoked</div>
          </div>
        </div>

        {/* License List */}
        {licenses.length === 0 ? (
          <div className="empty-state-small">
            <p>No licenses assigned to this user yet.</p>
            <p className="muted">Generate new licenses and assign them to this user.</p>
          </div>
        ) : (
          <div className="tenant-list">
            {licenses.slice(0, 10).map((license) => (
              <div key={license.id} className="tenant-assignment-item">
                <div className="tenant-info">
                  <Link to={`/licenses/${license.id}`} className="tenant-name" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {license.licenseKey}
                  </Link>
                  {license.device && (
                    <Link to={`/ggos-devices/${license.device.thingName}`} className="tenant-plan" style={{ marginLeft: '0.5rem' }}>
                      {license.device.thingName}
                    </Link>
                  )}
                </div>
                <div className="tenant-role">
                  <span className={`status-badge ${getLicenseStatusBadge(license.status).class}`}>
                    {getLicenseStatusBadge(license.status).label}
                  </span>
                </div>
                <Link to={`/licenses/${license.id}`} className="btn btn-secondary btn-sm">
                  View
                </Link>
              </div>
            ))}
            {licenses.length > 10 && (
              <div style={{ textAlign: 'center', padding: '0.5rem', color: '#666' }}>
                ... and {licenses.length - 10} more licenses. <Link to={`/licenses?ownerType=user&ownerId=${user?.id}`}>View all</Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Login Activity */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Recent Login Activity</h2>
          <Link to={`/audit-log?userId=${user?.id}`} className="btn btn-secondary btn-sm">
            View Full Log
          </Link>
        </div>
        {(!user?.recentAuditLog || user.recentAuditLog.length === 0) ? (
          <div className="empty-state-small">
            <p>No login activity recorded yet.</p>
            <p className="muted">Activity will appear here once the user logs in via login.galigali.in</p>
          </div>
        ) : (
          <div className="data-table" style={{ fontSize: '0.85rem' }}>
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>IP Address</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {user.recentAuditLog.slice(0, 10).map((event) => {
                  const eventLabels = {
                    'login_success': { label: 'Login' },
                    'signup': { label: 'Signup' },
                    'otp_requested': { label: 'OTP Sent' },
                    'otp_failed': { label: 'OTP Failed' },
                    'token_refresh': { label: 'Token Refresh' },
                    'logout': { label: 'Logout' },
                    'session_resume': { label: 'Session Resume' },
                    'admin_create': { label: 'Admin Created' },
                    'admin_migrate': { label: 'Admin Migrated' },
                    // V276 approval-gate + product lifecycle events
                    'login_blocked': { label: 'Login Blocked (approval gate)' },
                    'access_approved': { label: 'Access Approved' },
                    'access_rejected': { label: 'Access Rejected' },
                    'contact_verify_requested': { label: 'Contact Verify Requested' },
                    'contact_verified': { label: 'Contact Verified' },
                    'auto_approved_via_invite': { label: 'Auto-Approved via Invite' },
                    'product_enrolled': { label: 'Product Enrolled' },
                    'product_unenrolled': { label: 'Product Unenrolled' },
                  };
                  const evt = eventLabels[event.eventType] || { label: event.eventType };
                  return (
                    <tr key={event.id}>
                      <td>{evt.label}</td>
                      <td className="mono" style={{ fontSize: '0.8rem' }}>{event.ipAddress || '-'}</td>
                      <td>{formatDate(event.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <EditProfileModal
          user={user}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateProfile}
          loading={actionLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          user={user}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => handleAction('delete')}
          loading={actionLoading}
        />
      )}

      {/* Tenant Assignment Modal */}
      {showTenantModal && (
        <TenantAssignmentModal
          user={user}
          onClose={() => setShowTenantModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowTenantModal(false); fetchUser(); }}
          onError={setError}
        />
      )}

      {/* Edit Permissions Modal */}
      {editPermissionsAssignment && (
        <EditPermissionsModal
          user={user}
          assignment={editPermissionsAssignment}
          onClose={() => setEditPermissionsAssignment(null)}
          onSuccess={(msg) => { setMessage(msg); setEditPermissionsAssignment(null); fetchUser(); }}
          onError={setError}
        />
      )}
    </div>
  );
};

export default UserDetail;
